/* eslint-disable max-lines-per-function, max-statements */
import React from "react";
import { useParams, useSearchParams } from "react-router-dom";
import { Typography, Divider, Link as MuiLink } from "@mui/material";
import { useApolloClient, useMutation, useQuery } from "@apollo/client";
import { useSnackbar } from "components/haven-design/snackbar";
import { usePostgresViewer } from "components/viewer/context";
import { navigate } from "utils/navigation";
import {
  mergePropertyContractUpdatedIntoList,
  PROPERTY_CONTRACT_UPDATED_SUBSCRIPTION,
} from "data/graphql/property-contract-updated-subscription";
import {
  propertyContractSettingsPath,
  shouldRedirectSignedOrphanContractToProperty,
} from "../sidebar-entries";
import { SettingsHeader } from "./settings-header";
import {
  ARCHIVE_MUTATION,
  ContractDetailsCard,
  MANAGER_CONTRACTS_ROUTE,
  PropertyContractsData,
  DeletePendingModal,
  PROPERTY_CONTRACTS_QUERY,
} from "./contracts-shared";
import { useContractDetailsActions } from "./use-contract-details-actions";

const contractDisplayName = (
  contract: { propertyName?: string | null; propertyLegalName?: string | null },
): string =>
  contract.propertyName || contract.propertyLegalName || "Contract";

const ContractsSettings: React.FC = () => {
  const { openSnackbar } = useSnackbar();
  const viewer = usePostgresViewer();
  const client = useApolloClient();
  const { contractId: selectedContractId } = useParams();
  const [searchParams] = useSearchParams();
  const fromList = searchParams.get("from") === "list";
  const redirectedContractIdRef = React.useRef<string | null>(null);
  const { data, loading, error, refetch, subscribeToMore } =
    useQuery<PropertyContractsData>(PROPERTY_CONTRACTS_QUERY, {
      fetchPolicy: "cache-first",
    });

  React.useEffect(() => {
    const unsubscribe = subscribeToMore({
      document: PROPERTY_CONTRACT_UPDATED_SUBSCRIPTION,
      updateQuery: (prev, { subscriptionData }) =>
        mergePropertyContractUpdatedIntoList(prev, subscriptionData),
    });
    return () => {
      unsubscribe();
    };
  }, [subscribeToMore]);

  const contracts = data?.propertyContracts || [];
  const selectedContract =
    contracts.find((contract) => contract.id === selectedContractId) || null;
  const [resolvingContract, setResolvingContract] = React.useState(false);
  const resolveAttemptedForId = React.useRef<string | null>(null);

  React.useEffect(() => {
    if (!selectedContractId || loading || selectedContract) {
      if (!selectedContractId) {
        resolveAttemptedForId.current = null;
      }
      setResolvingContract(false);
      return () => {};
    }
    if (resolveAttemptedForId.current === selectedContractId) {
      return () => {};
    }
    resolveAttemptedForId.current = selectedContractId;
    let cancelled = false;
    setResolvingContract(true);
    refetch()
      .catch(() => {})
      .finally(() => {
        if (!cancelled) {
          setResolvingContract(false);
        }
      });
    return () => {
      cancelled = true;
    };
  }, [selectedContractId, loading, selectedContract, refetch]);

  React.useEffect(() => {
    redirectedContractIdRef.current = null;
  }, [selectedContractId]);

  React.useEffect(() => {
    if (!shouldRedirectSignedOrphanContractToProperty(selectedContract, { fromList })) {
      return;
    }
    const listId = selectedContract?.interestListId;
    if (!listId || !selectedContract) {
      return;
    }
    if (redirectedContractIdRef.current === selectedContract.id) {
      return;
    }
    redirectedContractIdRef.current = selectedContract.id;
    const redirectToPropertyContract = async () => {
      if (viewer?.organizationId) {
        await client.refetchQueries({ include: ["getOrgLists"] }).catch(() => {});
      }
      navigate(propertyContractSettingsPath(listId), { replace: true });
    };
    redirectToPropertyContract();
  }, [client, fromList, selectedContract, viewer?.organizationId]);

  const {
    busyId,
    signedContractId,
    reminderSentForContractId,
    reminderFailure,
    showSignNow,
    handleResendConfirm,
    handleSignNow,
  } = useContractDetailsActions(selectedContract, refetch);

  const [archiving, setArchiving] = React.useState(false);
  const [archiveConfirmOpen, setArchiveConfirmOpen] = React.useState(false);

  const [archiveMutation] =
    useMutation(ARCHIVE_MUTATION, {
      refetchQueries: [{ query: PROPERTY_CONTRACTS_QUERY }],
      awaitRefetchQueries: true,
    });

  const handleArchiveDraft = () => {
    if (!selectedContract || !["draft", "sent"].includes(selectedContract.status ?? "")) {
      return;
    }
    setArchiveConfirmOpen(true);
  };

  const handleArchiveConfirm = async () => {
    if (!selectedContract) return;
    setArchiving(true);
    try {
      await archiveMutation({ variables: { id: selectedContract.id } });
      setArchiveConfirmOpen(false);
    } catch (err: unknown) {
      const message = err instanceof Error ? err.message : "Failed to archive";
      openSnackbar({ message, status: "error", duration: 4000 });
    } finally {
      setArchiving(false);
      refetch();
    }
  };

  let detailsContent: React.ReactNode = null;
  if (loading || resolvingContract) {
    detailsContent = <Typography variant="body1">Loading…</Typography>;
  } else if (selectedContractId && selectedContract) {
    detailsContent = (
      <ContractDetailsCard
        selectedContract={selectedContract}
        busyId={busyId}
        signedContractId={signedContractId}
        reminderSentForContractId={reminderSentForContractId}
        reminderFailure={reminderFailure}
        archiving={archiving}
        onResend={handleResendConfirm}
        {...(showSignNow ? { onSignNow: handleSignNow } : {})}
        onDeleteDraft={handleArchiveDraft}
      />
    );
  } else if (selectedContractId) {
    detailsContent = <Typography variant="body1">Contract not found.</Typography>;
  }

  const sidebarHeaderTitle = selectedContract ? `${contractDisplayName(selectedContract)} Contract` : "Contract";

  return (
    <div>
      {fromList && (
        <Typography variant="h6" component="div" sx={{ margin: "10px 0px" }}>
          <MuiLink href={MANAGER_CONTRACTS_ROUTE} color="primary">
            Contracts
          </MuiLink>
          {selectedContract && (
            <>
              {" "}
              &gt; {contractDisplayName(selectedContract)}
            </>
          )}
        </Typography>
      )}
      {!fromList && <SettingsHeader title={sidebarHeaderTitle} />}
      {error && (
        <Typography variant="body2" color="error" gutterBottom>
          {error.message}
        </Typography>
      )}
      {fromList && <Divider sx={{ mb: 2 }} />}
      <div style={{ paddingTop: fromList ? 0 : 16 }}>
        {detailsContent}
      </div>

      <DeletePendingModal
        isOpen={archiveConfirmOpen}
        archiving={archiving}
        onClose={() => setArchiveConfirmOpen(false)}
        onConfirm={handleArchiveConfirm}
      />
    </div>
  );
};

export default ContractsSettings;
